import { useEffect, useState } from 'react';
import { User, Mail, Phone, MapPin, Briefcase, Link as LinkIcon, Twitter, Linkedin, Instagram, Save, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import profileAPI from '../../services/profileApi';

const FIELDS = [
  { key: 'name', label: 'Full Name', Icon: User, placeholder: 'Your full name' },
  { key: 'email', label: 'Email', Icon: Mail, readOnly: true },
  { key: 'phone', label: 'Phone', Icon: Phone, placeholder: '+1 555 000 0000' },
  { key: 'country', label: 'Location', Icon: MapPin, placeholder: 'City, Country' },
  { key: 'occupation', label: 'Occupation', Icon: Briefcase, placeholder: 'e.g. Day trader' },
  { key: 'website', label: 'Website', Icon: LinkIcon, placeholder: 'https://…' },
  { key: 'twitter', label: 'X / Twitter', Icon: Twitter, placeholder: '@handle' },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, placeholder: 'profile-slug' },
  { key: 'instagram', label: 'Instagram', Icon: Instagram, placeholder: '@handle' },
];
const EDITABLE = FIELDS.filter((f) => !f.readOnly).map((f) => f.key).concat('bio');

const inputCls = 'w-full px-4 py-2 bg-gray-900/50 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500';

export const ProfileInfoForm = ({ profile, editing, setEditing, onSaved }) => {
  const [form, setForm] = useState(profile);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setForm(profile); }, [profile, editing]);

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Name cannot be empty');
    const payload = Object.fromEntries(EDITABLE.map((k) => [k, (form[k] || '').trim()]));
    setSaving(true);
    try {
      await profileAPI.update(payload);
      toast.success('Profile updated');
      setEditing(false);
      onSaved(payload);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace('Value error, ', '') : d || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const display = (f) => {
    const v = profile[f.key];
    if (!v) return <p className="text-gray-500 italic text-sm">Not provided</p>;
    return <p className="text-white font-semibold break-words">{v}</p>;
  };

  return (
    <div className="glow-card p-6 rounded-xl" data-testid="profile-info-card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl sm:text-2xl font-bold">Personal Information</h2>
        {editing && (
          <div className="flex gap-2">
            <button type="button" onClick={() => setEditing(false)} data-testid="profile-cancel-btn" className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 text-sm">
              <X className="h-4 w-4" /> Cancel
            </button>
            <button type="button" onClick={save} disabled={saving} data-testid="profile-save-btn" className="px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white rounded-lg flex items-center gap-2 text-sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-sm text-gray-400 mb-2 flex items-center gap-2"><f.Icon className="h-4 w-4" />{f.label}</label>
            {editing && !f.readOnly ? (
              <input value={form[f.key] || ''} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} data-testid={`profile-input-${f.key}`} />
            ) : (
              <div data-testid={`profile-field-${f.key}`}>{display(f)}</div>
            )}
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="text-sm text-gray-400 mb-2 block">Bio</label>
          {editing ? (
            <textarea value={form.bio || ''} maxLength={500} placeholder="Tell others about your trading style" onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${inputCls} min-h-[100px]`} data-testid="profile-input-bio" />
          ) : (
            <div data-testid="profile-field-bio">{display({ key: 'bio' })}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileInfoForm;
